import PromptInputType from "common/enums/PromptInputType";
import DataType from "common/enums/DataType";
import DependencyExecutor from "common/widgets/PromptList/DependencyExecutor";
import DefaultValueApply from "common/widgets/PromptList/DefaultValueApply";
import HorizontalAlign from "common/enums/form/responsive/HorizontalAlign";
import VerticalAlign from "common/enums/form/responsive/VerticalAlign";
import sizeOfForm from "common/forms/responsive/sizeOfForm";
import accessibilityTree from "common/forms/accessibilityTree";
import AccessibilityTreeType from "common/enums/form/AccessibilityTreeType";
import TabIndexCapture from "common/helper/TabIndexCapture";
export default {

    options: {
        // whether prompt inputs accept focus or not
        tabbable: true,

        // whether validation applies to the PromptList impl (used on absolute positioning z-index)
        canValidate: true
    },

    // the id of the current focused PromptInput
    _currentFocusId: undefined,

    onInit: function(options) {
        this.promptWidgets = [];
        this.rows = [];

        //Focus internal to the prompt list is handled completely manually
        //As a result, the prompt list should only appear in the global tab order when focus is NOT inside of it
        this.focusCapture = new TabIndexCapture(this.element);
        this.element.on("focusout.promptlist", () => {
            this.focusCapture.release();
        });
        this.element.on("focusin.promptlist", () => {
            this.focusCapture.capture();
        });


        this.element.addClass("promptList");
        this.element.attr("role", this.element.attr("role") || "form");
        this.setTabIndex();

        //ensure ID constraint is met
        var orig_id = this.element.attr("id");
        if (orig_id === undefined) {
            console.error("PromptList widget requires an ID");
        }

        // Determine whether the element we are rendering on is a table or tbody.
        // If so, we will render <tr> for each prompt,
        var tag = this.element.prop("tagName").toUpperCase();
        this.options.isTable = tag == "TABLE" || tag == "TBODY";

        this.options.validateOnBlur = options.validateOnBlur === true;
        this.options.enterMovesFocus = options.enterMovesFocus === true;
        this.options.useDatasourcePlaceholders = options.useDatasourcePlaceholders === true;
        this.options.cacheListValues = options.cacheListValues === true;
        this.options.responsive = options.responsive ? options.responsive : false;

        this.element.bind("focusout", $.proxy(this._focusOut, this));
        this.refresh = $.proxy(this.refresh, this);
        this.options.autoBind = options.autoBind !== false;
        this.setDataSource(options.dataSource);
    },

    /**
     * Set the tab index of the widget
     */
    setTabIndex: function() {
        this.element.attr("tabindex", "-1");
    },

    onSetDataSource: function(ds) {
        if (this.dataSource) {
            this.dataSource.unbind("change", this.refresh);
        }
        if (this.dependencies) {
            this.dependencies.destroy();
            delete this.dependencies;
        }
        this.options.dataSource = this.dataSource = kendo.data.DataSource.create(ds);
        this.dataSource.bind("change", this.refresh);
        this.initDependencies();
        if (this.options.autoBind) {
            this.dataSource.fetch();
        }
    },

    /**
     * initialize the dependency executor, this can be overridden
     */
    initDependencies: function() {
        this.dependencies = new DependencyExecutor(this.dataSource);
    },

    _preventing: false,

    preventRefresh: function(promise) {
        var me = this;
        me._preventing = true;
        promise.always(function() {
            me._preventing = false;
        })
    },

    refresh: function(e) {
        if (!this._preventing && (!e || e.action !== "itemchange")) {
            this.clear();
            this.drawPrompts();
            this._finishRefresh();
        } else if (e) {
            if (e.field === "data" && e.items && e.items[0]) {
                setTimeout(() => {
                    this._rebuildPrompt(e.items[0]);
                }, 0);
            } else if (e.field === "hidden" || e.field === "disabled" || e.field === "readonly" || e.field === "required" || e.field === "loading") {
                setTimeout(() => {
                    this.restoreFocus();
                }, 0);
            }
        }
    },

    getFinished: function() {
        return this.dependencies.getFinished();
    },

    _finishRefresh: function() {
        this.silentValidate();
        this.readonly(this.options.readonly, true);
        this.enable(this.options.enabled);
        this.tabbable(this.options.tabbable);
        this.restoreFocus();
        this._listValuesUpdated();
    },

    clear: function() {
        var children = this.element.children();
        for (var i = 0; i < children.length; i++) {
            kendo.destroy(children[i]);
        }
        this.element.find("*").off();
        this.element.empty();
        this.promptWidgets = [];
        this._rows = [];
    },

    /**
     * Draw prompts, from prompt array.
     */
    drawPrompts: function() {
        var me = this;
        var prompts = this.dataSource.view();

        this.dependencies.ignore(function() {
            DefaultValueApply.applyDefaultValues(prompts);
        });

        if(this.options.accessibleText && this.options.responsive) {
            //This mode of form rendering converts to a tree for accessibility purposes.
            //It will attempt to group controls with labels and descriptions
            const tree = accessibilityTree(prompts, this.options.responsive);
            this.drawTree(tree, this.element);
        } else {
            const z = prompts
                .filter((prompt) => prompt.hasOwnProperty("y"))
                .map((prompt) => prompt.y)
                .sort((y1, y2) => {
                    return y1 - y2;
                })
                .filter((y, index, array) => array.indexOf(y) === index)
                .map((y, index) => ({y, i: index}))
                .reduce((reduced, current) => (reduced[current.y] = current.i, reduced), {});
            prompts.forEach((prompt) => {
                me.drawPrompt(prompt, this.element, [], z);
            });
        }

        if (this.options.responsive) {
            this.setSizeOfForm(sizeOfForm(prompts, {
                maxWidth: 600,
                padding: this.options.responsive.spaceElements
            }) + "px");
        } else {
            this.setSizeOfForm("100%");
        }
    },

    drawTree: function(nodes, renderIn) {
        const toIDList = (prompts) => prompts.map((p) => this._getPromptInputId(p));
        nodes.forEach((node) => {
            if(node.type === AccessibilityTreeType.GROUP) {
                const group = $("<div>", {
                    "role": "group",
                    "aria-labelledby": toIDList(node.labelledBy).join(" "),
                    "aria-describedby": toIDList(node.describedBy).join(" ")
                });
                renderIn.append(group);
                this.drawTree(node.children, group);
            } else {
                //type == CONTROL
                this.drawPrompt(node.control, renderIn, toIDList(node.labelledBy));
            }
        })
    },


    _sizeOfForm: "100%",
    setSizeOfForm: function(size) {
        this._sizeOfForm = size;
        this.triggerMaxWidth();
    },

    _borderSize: "0px",
    setBorderSize: function(border) {
        this._borderSize = border;
        this.triggerMaxWidth();
    },

    triggerMaxWidth: function() {
        var size = this._sizeOfForm;
        if (this._borderSize && this._borderSize !== "0px" && this.options.responsive) {
            size = "calc(" + this._sizeOfForm + " + " + this._borderSize + " + " + this._borderSize + ")";
        }
        this.trigger("maxWidth", {
            size: size
        });
    },

    _rows: [],

    _getRow: function(row, align, vAlign, renderIn) {
        var index = parseInt(row, 10);
        var container = $("<div>").addClass("row");
        if (align === HorizontalAlign.CENTER) {
            container.addClass("centerAligned");
        } else if (align === HorizontalAlign.RIGHT) {
            container.addClass("rightAligned");
        } else if (align === HorizontalAlign.SPLIT) {
            container.addClass("splitAligned");
        } else {
            container.addClass("leftAligned");
        }

        if (vAlign === VerticalAlign.CENTER) {
            container.addClass("vCenterAligned");
        } else if (vAlign === VerticalAlign.BOTTOM) {
            container.addClass("vBottomAligned");
        } else {
            container.addClass("vTopAligned");
        }

        if (isNaN(index)) {
            //if row isn't specified, always insert a new container
            index = Number.MAX_VALUE;
        } else {
            for (var i = 0; i < this._rows.length; i++) {
                if (this._rows[i].index === index) {
                    //Found an existing container with matching row
                    return this._rows[i].container;
                } else if (index < this._rows[i].index) {
                    //this._rows is order by index, so this happening means there is no matching existing container
                    if(this._rows[i].renderIn[0] === renderIn[0]) {
                        //its possible for multiple rows to exist in group, when that happens ensure order is maintained
                        container.insertBefore(this._rows[i].container);
                    } else {
                        renderIn.append(container);
                    }
                    this._rows.splice(i, 0, {
                        container: container,
                        renderIn: renderIn,
                        index: index
                    });
                    return container;
                }
            }
        }

        //Either a new container is always needed(row = null) or there is no match and it needs inserted at the end
        renderIn.append(container);
        this._rows.push({
            container: container,
            renderIn: renderIn,
            index: index
        });
        return container;
    },

    /**
     * Draw a single prompt to the list
     * @param prompt
     * @param renderIn DOM element to render in
     * @param labels Label IDs
     */
    drawPrompt: function(prompt, renderIn, labels, z = {}) {
        if (prompt.hiddenMacro || !prompt.hidden) {
            // If we are rendering on a table, insert a <tr> for the prompt,
            // and set the target element to be the <tr>.
            // Else, create a span and set it as the target element
            var targetElement;
            if (this.options.isTable) {
                var row = $("<tr>").addClass("singlePromptContainer");
                renderIn.append(row);
                targetElement = row;
            } else {
                var span = $("<span>").addClass("singlePromptContainer");
                if (this.options.responsive) {
                    span.css("padding", this.options.responsive.spaceElements + "px " + this.options.responsive.spaceElements + "px");
                    this._getRow(prompt.get("row"), prompt.get("rowAlign"), prompt.get("rowVAlign"), renderIn).append(span);
                } else {
                    renderIn.append(span);
                }
                targetElement = span;
            }

            if (!this.options.responsive &&
                prompt.x !== undefined && prompt.x !== null && prompt.x >= 0 && prompt.y !== undefined && prompt.y !== null && prompt.y >= 0) {
                targetElement.css("position", "absolute");
                targetElement.css("left", prompt.x + "px");
                targetElement.css("top", prompt.y + "px");
                if(this.options.canValidate) {
                    targetElement.css("z-index", 5000 - z[prompt.y]);
                }
            }
            return this._renderInput(targetElement, prompt, undefined, undefined, labels);
        }
    },

    _renderInput: function(targetElement, prompt, replace, index, labels) {
        var me = this;
        var input;
        // Create input field based on input type
        switch (prompt.inputType) {
            case PromptInputType.BUTTON:
                input = me.drawButtonInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.TABLE:
                input = me.drawTableInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.H_LINE:
            case PromptInputType.V_LINE:
                input = me.drawLineInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.IMAGE:
                input = me.drawImageInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.DATE_FIELD:
                input = me.drawDateInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.SWITCH:
                input = me.drawSwitchInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.LIST:
                input = me.drawListInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.LIST_VIEW:
                input = me.drawListViewInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.READ_ONLY:
                input = me.drawReadOnlyInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.TEXT_AREA:
                input = me.drawTextAreaInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.TEXT_FIELD:
                {
                    switch (prompt.dataType) {
                        case DataType.DECIMAL:
                            input = me.drawDecimalInput(targetElement, prompt, replace, index, labels);
                            break;
                        case DataType.INTEGER:
                            input = me.drawIntegerInput(targetElement, prompt, replace, index, labels);
                            break;
                        case DataType.STRING:
                        default:
                            input = me.drawStringInput(targetElement, prompt, replace, index, labels);
                            break;
                    }
                    break;
                }
            case PromptInputType.DYNAMIC_FIELD:
                input = me.drawDynamicInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.LINK:
                input = me.drawLinkInput(targetElement, prompt, replace, index, labels);
                break;
            case PromptInputType.FILE_UPLOAD:
                input = me.drawFileUploadInput(targetElement, prompt, replace, index, labels);
                break;
        }
        return input;
    },

    /**
     * Create the label for the prompt input.
     * @param prompt
     */
    drawLabel: function(targetElement, prompt) {
        var lbl = ($('<label/>', {
            for: this._getElementIdPrefix() + "_" + prompt.uid,
            "data-field": prompt.name,
            title: prompt.label
        }));
        lbl.attr("id", this._getElementIdPrefix() + "_" + prompt.uid + "_label");
        lbl.append($("<span>", {
            "class": "labelText",
            text: prompt.label
        }));

        this.setPromptControlStyles(targetElement, prompt, lbl);

        // append the label to the target element (either parent element or tr)
        targetElement.append(lbl);

        // if we are rendering a table, wrap the label in a <td>
        if (this.options.isTable) {
            lbl.wrap("<td/>")
        }

        return lbl;
    },

    _getElementIdPrefix: function() {
        return this.element.attr("id");
    },

    _getPromptInputId: function(prompt) {
        return this._getElementIdPrefix() + "_" + prompt.get("uid")
    },

    // override to set styles on render of the individual prompt
    setPromptControlStyles: function(targetElement, prompt, lbl) {

    },

    /**
     * This is a generic method that can be used for each of the below prompt types
     * @param targetElement element to draw
     * @param prompt prompt model
     * @param Type Subclass of PromptInput to construct
     * @param label Optional. False if label should be ommitted
     * @param replace Optional.  If truthy, treated as a replace, does not append new div to the DOM.
     * @param index {integer} The index in the promptWidgets array where the replace will go
     * @param labels The prompts that label this element
     */
    drawInput: function(targetElement, prompt, Type, label, replace, index, labels) {

        // Draw label unless instructed not to, and only if we are not replacing.
        var labelElement;
        if (label !== false) {
            if (replace === undefined) {
                labelElement = this.drawLabel(targetElement, prompt);
                labels = [labelElement.attr("id"), ...labels];
            } else {
                labelElement = replace.label;
            }
        }

        // create the main element for the widget.
        var div = $("<div></div>");

        // If replacing, replace the given element with the new one.
        // Otherwise, append the new element to the DOM and wrap it if necessary.
        if (replace) {
            replace.element.replaceWith(div);
        } else {
            // If labelPosition is set, then we are defining per-prompt where the label should be displayed.
            // Otherwise, always append the input to the parent, and we are handling styling of the positioning
            // automatically, based on the parent container (table vs div layouts).
            if (prompt.labelPosition && (prompt.labelPosition == "BOTTOM" || prompt.labelPosition == "RIGHT")) {
                // prepend the div to the target element
                targetElement.prepend(div);
            } else {
                // append the div to the target element (either parent element or tr)
                targetElement.append(div);
            }

            // if we are rendering a table, wrap the div in a <td>
            if (this.options.isTable) {
                div.wrap("<td/>");
            }
        }

        // Based on the type we were given (kendoStringPromptInput, etc), initialize the kendo widget.
        // Since the type is dynamic, this is like we were calling div.kendoStringPromptInput on the div.
        var input = new Type(div, {
            ...this.promptInputOptions(),
            model: prompt,
            id: prompt.uid,
            parentId: this._getElementIdPrefix(),
            labelledBy: labels,
            validateOnBlur: this.options.validateOnBlur,
            enterMovesFocus: this.options.enterMovesFocus,
            tabbable: this.options.tabbable,
            disabledWhenReadonly: this.options.disabledWhenReadonly,
            responsive: this.options.responsive,
            usePredictiveTabbing: this.options.usePredictiveTabbing,
            emptyInfo: this.options.emptyInputInfo,
            valueParse: $.proxy(this.valueParsed, this)
        });

        input.label = labelElement;
        input.wrapper = targetElement;

        if (this.options.responsive) {
            this._updateFlex(input);
        } else {
            input.updateSize();
        }

        input.onInit(this);

        this._addInput(input, prompt, index);

        return input;
    },

    promptInputOptions() {
        return {};
    },

    _updateFlex: function(input) {

    },

    _addInput: function(input, prompt, index) {
        input.bind("inputChange", $.proxy(this.inputChanged, this));
        input.bind("inputSelect", $.proxy(this._inputSelected, this));
        input.bind("focusPrev", $.proxy(this.focusPrev, this));
        input.bind("focusNext", $.proxy(this.focusNext, this));
        input.bind("inputFocus", $.proxy(this.inputFocused, this));

        if (prompt.tooltip) {
            input.element.attr("title", prompt.tooltip);
        }

        // if validateOnBlur is true, bind to blur.
        // This should only be true when we're using a CustomPromptList/CustomValidator.
        if (this.options.validateOnBlur) {
            input.bind("blur", () => {
                if(input.options.id === this._currentFocusId || input.options.id === this._focusOutId) {
                    this.validateInput(prompt, true);
                }
                delete this._focusOutId;
            });
        }

        if (index === undefined) {
            this.promptWidgets.push(input);
        } else {
            this.promptWidgets.splice(index, 0, input);
        }
    },

    inputChanged: function(e) {

        this.onInputChanged(e);

        this.trigger("change");
        this.trigger("userChange");
    },

    /**
     * Handler for a selection made on a dropdown or combobox
     * @param e
     * @private
     */
    _inputSelected: function(e) {
        if (this._isLastEditablePromptInput(e.sender.options.id) && !this._isAnyPromptLoading()) {
            if(this.trigger("lastPromptValueSelected")) {
                this._wasSelectPrevented = true;
            }
        }
    },

    /**
     * Whether the provided prompt is the last editable prompt
     * @param promptId
     * @private
     */
    _isLastEditablePromptInput: function(promptId) {
        var nextPromptInput = this._getNextPromptInput(promptId);
        if (nextPromptInput) {
            // async values need to be verified so this is not the last input
            if (nextPromptInput.isLoading()) {
                return false;
            } else if (!nextPromptInput.canFocus() || !nextPromptInput.canFocusDatasource()) {
                // if next prompt is not loading and it can't be focused, check if there are prompts after it that can be focused
                return this._isLastEditablePromptInput(nextPromptInput.options.id);
            }
            return false;
        } else {
            return true;
        }
    },

    /**
     * Whether any prompt is loading
     * @private
     */
    _isAnyPromptLoading: function() {
        return this.promptWidgets.some(function(promptInput) {
            return promptInput.isLoading();
        });
    },

    // ---------- The following create the input prompts for each of the input type ---------- //

    drawStringInput: function(targetElement, prompt, replace, index, labels) {
        if (!prompt.get("blindEntry")) {
            return this.drawPlainStringInput(targetElement, prompt, replace, index, labels);
        } else {
            return this.drawBlindStringInput(targetElement, prompt, replace, index, labels);
        }
    },

    drawIntegerInput: function(targetElement, prompt, replace, index, labels) {
        if (!prompt.get("blindEntry")) {
            return this.drawPlainIntegerInput(targetElement, prompt, replace, index, labels);
        } else {
            return this.drawBlindIntegerInput(targetElement, prompt, replace, index, labels);
        }
    },

    drawDateInput: function(targetElement, prompt, replace, index, labels) {
        if (!prompt.get("blindEntry")) {
            return this.drawPlainDateInput(targetElement, prompt, replace, index, labels);
        } else {
            return this.drawBlindDateInput(targetElement, prompt, replace, index, labels);
        }
    },

    drawDecimalInput: function(targetElement, prompt, replace, index, labels) {
        if (!prompt.get("blindEntry")) {
            return this.drawPlainDecimalInput(targetElement, prompt, replace, index, labels);
        } else {
            return this.drawBlindDecimalInput(targetElement, prompt, replace, index, labels);
        }
    },

    /**
     * dynamic inputs are used in indexing for controls where we cannot determine on the server side whether
     * to use a typed input or a drop down list.  If the control has a datasource with dependencies, and the
     * independent fields don't have a value, the server will not be able to execute the datasource right away,
     * and it will indicate dynamic field.
     *
     * The first time we draw the field, we must draw its singular representation using the data type.
     * So for example, user will see a text field or date picker, and a placeholder.
     *
     * After an independent field gets a value, the DependencyExecutor will handle running datasources.
     * When the datasource returns, DependencyExecutor will set the data field on this prompt, which
     * will trigger a refresh/redraw of the prompt list.
     *
     * At that point, the prompt model has data items in its data field.  We can use those items to determine
     * whether to render a list, or to continue rendering the singular input based on data type.
     *
     * @param targetElement
     * @param prompt the prompt model
     * @param replace
     * @param index {integer} The index in the promptWidgets array where the replace will go
     * @param labels
     */
    drawDynamicInput: function(targetElement, prompt, replace, index, labels) {

        // first, figure out if we previously rendered a list.  If yes, keep the list.
        // If not, figure out if the prompt model has a data field defined.
        // If it does, figure out how many items are in the data.
        // If there is more than one item, draw a list.
        // In all other cases, draw the singular input.
        var input;
        prompt.readonly = prompt.originallyReadOnly;
        if (prompt.data && prompt.data.length > 1) {
            // set a marker to indicate that this input has been rendered as a list.
            // This allows future refresh operations to keep a list for dynamic fields, regardless of the number of
            // results returned by the datasource.
            prompt.set("renderAsList", true);

            input = this.drawListInput(targetElement, prompt, replace, index, labels);
        } else {
            // if the datasource has not executed, use the original readonly value
            // otherwise enable the input if the user is allowed to edit the datasource values and the field is editable
            prompt.readonly = !(prompt.originallyReadOnly !== true && prompt.datasourceEditable === true);
            switch (prompt.dataType) {
                case DataType.DECIMAL:
                    input = this.drawDecimalInput(targetElement, prompt, replace, index, labels);
                    break;
                case DataType.INTEGER:
                    input = this.drawIntegerInput(targetElement, prompt, replace, index, labels);
                    break;
                case DataType.DATE:
                    input = this.drawDateInput(targetElement, prompt, replace, index, labels);
                    break;
                case DataType.STRING:
                default:
                    input = this.drawStringInput(targetElement, prompt, replace, index, labels);
                    break;
            }

            // figure out whether to show a placeholder.
            if (this.options.useDatasourcePlaceholders) {
                input.toggleDatasourcePlaceholder();
            }
        }
        return input;
    },

    getHeight: function() {
        return this.promptWidgets.reduce(function(max, widget) {
            return Math.max(max, window.pageYOffset + widget.wrapper[0].getBoundingClientRect().bottom);
        }, 0);
    },

    onDestroy: function() {
        if (this.dataSource && this.dataSource.unbind) {
            this.dataSource.unbind("change");
        }
        this.element.off(".promptlist");
        delete this.focusCapture;

        this.clear();
    },

    enable: function(to) {
        if (this.options.disabledWhenReadonly) {
            return;
        }
        this.options.enabled = to === true;
        this.promptWidgets.forEach(function(promptInput) {
            if (promptInput && promptInput.enable) {
                promptInput.enable(this.options.enabled);
            }
        }, this);
    },

    readonly: function(to, force) {
        var old = this.options.readonly;
        this.options.readonly = to === true;
        if (old !== this.options.readonly || force) {
            this.promptWidgets.forEach(function(promptInput) {
                if (promptInput && promptInput.readonly) {
                    promptInput.readonly(this.options.readonly);
                }
            }, this);
        }
    },

    /**
     * Update the tabbable option making the inputs focusable or not
     * @param to {boolean} True to have inputs accept focus
     */
    tabbable: function(to) {
        this.options.tabbable = to !== false;
        this.promptWidgets.forEach(function(promptInput) {
            promptInput.tabbable(this.options.tabbable);
        }, this);
    },

    /**
     * Set the focus to the first input.
     */
    focus: function() {
        if (!this.options.readonly && this.options.enabled) {
            //First try and focus something based on tabIndex=0, then move to attempt focus based on order in DOM
            // tabindex 0 is used in indexing for the prompt that is assigned initial focus
            var models = this.dataSource.view();
            for (var j = 0; j < models.length && j < this.promptWidgets.length; j++) {
                if (models[j].get("tabIndex") == 0 && this.promptWidgets[j].canFocusInitially()) {
                    this.focusPromptInput(this.promptWidgets[j]);
                    return;
                }
            }
            for (var i = 0; i < this.promptWidgets.length; i++) {
                if (this.promptWidgets[i].canFocusInitially()) {
                    this.focusPromptInput(this.promptWidgets[i]);
                    return;
                }
            }
        }
        this.element.focus();
    },

    /**
     * Restore focus to the prompt input that had focus before the datasource executed
     */
    restoreFocus: function() {
        var promptInput = this.getFocusedInput();
        // if the current focus is on an input that is still loading, don't let other refreshes intercept the activity
        if (!promptInput || promptInput.isLoading()) {
            return;
        }
        if (promptInput.canFocus() && promptInput.canFocusDatasource()) {
            this.focusPromptInputRestore(promptInput);
        } else if (this._focusDirection === "prev") {
            this._focusPrevRestore();
        } else if (this._focusDirection === "next") {
            this._focusNext();
        }
    },

    /**
     * Get the focused prompt
     * @param promptId {string} Optional - the model id of the prompt
     * @returns {PromptInput}
     */
    getFocusedInput: function(promptId) {
        var id = promptId !== undefined ? promptId : this._currentFocusId;
        var focused = $.grep(this.promptWidgets, function(prompt) {
            return prompt.options.id === id;
        });
        return focused[0];
    },

    /**
     * Focus the prompt input
     * @param promptInput {PromptInput} The prompt to focus
     * @param isReverse {boolean} Whether the tab order was in reverse or not (for focusing the last focusable in a prompt)
     */
    focusPromptInput: function(promptInput, isReverse) {
        promptInput.focus(isReverse);
        this._inputFocused(promptInput);
    },

    focusPromptInputRestore: function(promptInput) {
        promptInput.restoreFocus();
        this._inputFocused(promptInput);
    },

    /**
     * Handler for the PromptInput inputFocus event
     * @param e
     */
    inputFocused: function(e) {
        this._inputFocused(e.sender);
    },

    valueParsed: function(e) {
        this.trigger("inputValueParse", {
            items: [
                e.sender.options.model
            ]
        });
    },

    /**
     * Tracks the current focused input
     * @param promptInput
     * @private
     */
    _inputFocused: function(promptInput) {
        this._currentFocusId = promptInput.options.id;
    },

    /**
     * Handler for the PromptInput focusPrev event
     * @param e {event} Prevented if a prompt was focused
     */
    focusPrev: function(e) {
        if (this._focusPrev()) {
            e.preventDefault();
        }
    },

    /**
     * Focus the previous prompt input
     * @param promptId {string} Optional - the prompt model id to get the previous prompt from
     * @param isRestore {boolean} Optional - flag for if this was called from restore or not. 47045
     * @returns {boolean} Whether a prompt was found or not
     * @private
     */
    _focusPrev: function(promptId, isRestore) {
        var prevPromptInput = this._getPrevPromptInput(promptId);
        if (prevPromptInput) {
            if (!this._canFocusInput(prevPromptInput)) {
                return this._focusPrev(prevPromptInput.options.id);
            }
            this._focusDirection = "prev";
            this.focusPromptInput(prevPromptInput, true);
            return true;
        } else {
            // first input is focused
            if (isRestore === true) {
                return this._focusNext();
            }
            this._currentFocusId = undefined;
            this._focusDirection = undefined;
            if(this.trigger("firstInputBlurred")) {
                return true;
            }
        }
        return false;
    },

    /**
     * Attempt to focus the previous prompt from restore.
     * If the prompt is not found, focus the next sibling so focus is not lost
     * @private
     */
    _focusPrevRestore: function() {
        return this._focusPrev(undefined, true);
    },

    /**
     * Gets the previous prompt input
     * @param promptId {string} Optional - the prompt model id to get the previous prompt from
     * @returns {PromptInput}
     * @private
     */
    _getPrevPromptInput: function(promptId) {
        var index = this.promptWidgets.indexOf(this.getFocusedInput(promptId));
        if (index > 0) {
            return this.promptWidgets[index - 1];
        }
    },

    /**
     * Handler for the PromptInput focusNext event
     * @param e {event} Prevented if a prompt was focused
     */
    focusNext: function(e) {
        if (this._focusNext()) {
            e.preventDefault();
        }
    },

    /**
     * Focus the next prompt input
     * @param promptId {string} Optional - the prompt model id to get the next prompt from
     * @returns {boolean} Whether a prompt was found or not
     * @private
     */
    _focusNext: function(promptId) {
        var nextPromptInput = this._getNextPromptInput(promptId);
        if (nextPromptInput) {
            if (!this._canFocusInput(nextPromptInput)) {
                return this._focusNext(nextPromptInput.options.id);
            }
            this._focusDirection = "next";
            this.focusPromptInput(nextPromptInput);
            return true;
        } else {
            // last input is focused
            var focused = this.getFocusedInput(promptId);
            if (focused.canFocus() && focused.canFocusDatasource() || promptId !== undefined) {
                var prevented = this._wasSelectPrevented || this.trigger("lastInputBlurred");
                delete this._wasSelectPrevented;
                if(prevented) {
                    this.clearFocus();
                }
                // needs to be prevented if move to next doc user setting is on
                return prevented;
            } else {
                return this._focusPrev();
            }
        }
    },

    /**
     * Whether the prompt can be focused currently
     * @param {Object} promptInput - The PromptInput
     * @private
     */
    _canFocusInput: function(promptInput) {
        return this.options.readonly && promptInput.canFocusWhenPromptListReadonly() || !this.options.readonly && (promptInput.isLoading() || promptInput.canFocus() && promptInput.canFocusDatasource());
    },

    /**
     * Handler for focusout. If focus goes to an element that is not a child of the promptlist, clear out focus
     * information so that refreshes triggered by outside controls do not focus the promptlist.
     * @param e
     * @private
     */
    _focusOut: function(e) {
        if (e.relatedTarget && !$.contains($(this.element)[0], e.relatedTarget) && e.relatedTarget.id !== this.element[0].id) {
            this._focusOutId = this._currentFocusId;
            this.clearFocus();
        }
    },

    /**
     * Clears our current focus information.
     * When a control outside of the promptlist triggers a refresh on the promptlist, focus will jump.
     * It can be useful to clear out focus info so that doesn't happen.
     */
    clearFocus: function() {
        this._currentFocusId = undefined;
        this._focusDirection = undefined;
    },

    /**
     * Gets the next prompt input
     * @param promptId {string} Optional - the prompt model id to get the next prompt from
     * @returns {PromptInput}
     * @private
     */
    _getNextPromptInput: function(promptId) {
        var index = this.promptWidgets.indexOf(this.getFocusedInput(promptId));
        if (index + 1 < this.promptWidgets.length) {
            return this.promptWidgets[index + 1];
        }
    },

    _rebuildPrompt: function(model) {
        // find the previous widget for this model, replace it with a new one.
        var existing = $.grep(this.promptWidgets, function(ex) {
            return ex.options.model.uid === model.uid;
        });

        var input;
        if (existing.length === 1) {
            existing = existing[0];
            var parent = existing.element.closest(".singlePromptContainer");
            const labels = existing.options.labelledBy;

            // destroy the previous widget
            existing.destroy();

            // remove existing from promptWidgets
            var index = this.promptWidgets.indexOf(existing);
            this.promptWidgets.splice(index, 1);

            // make a new widget, without drawing the label, etc.
            input = this._renderInput(parent, model, existing, index, labels);
            this._finishRefresh();
        }
        return input;
    },

    _listValuesUpdated: function() {
        if (this.options.cacheListValues) {
            var lists = this.dataSource.view().filter(function(prompt) {
                return prompt.get("renderAsList") || prompt.get("inputType") === PromptInputType.LIST;
            });
            if (lists.length > 0) {
                this.trigger("listValuesUpdated", {
                    lists: lists
                });
            }
        }
    }
}
